import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  FolderOpen, 
  MessageSquare, 
  FileText, 
  LogOut,
  ChevronRight,
  Settings,
  Image,
  Briefcase,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/categories', label: 'Kategoriler', icon: FolderOpen },
    { href: '/admin/messages', label: 'Mesajlar', icon: MessageSquare },
    { href: '/admin/job-applications', label: 'İş Başvuruları', icon: Briefcase },
    { href: '/admin/personnel-requests', label: 'Personel Talepleri', icon: Users },
    { href: '/admin/media', label: 'Medya Galerisi', icon: Image },
    { href: '/admin/settings', label: 'Site Ayarları', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path || 
    (path === '/admin/dashboard' && location.pathname === '/admin');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar */}
      <aside className="w-64 admin-sidebar flex flex-col" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex flex-col items-start gap-2" data-testid="admin-logo">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_personnel-staffing/artifacts/0igo1bgl_1000283641.png" 
              alt="Eta Global Group" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <span className="text-xs text-white/60 tracking-widest">
              ADMİN PANEL
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  data-testid={`admin-menu-${item.href.split('/').pop()}`}
                  className={`admin-menu-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.href)
                      ? 'bg-white/15 text-white border-l-4 border-white -ml-1 pl-5'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                  {isActive(item.href) && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">
                {admin?.name || 'Admin'}
              </p>
              <p className="text-white/60 text-xs truncate">
                {admin?.email || ''}
              </p>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 gap-3"
          >
            <LogOut className="w-5 h-5" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {menuItems.find(item => isActive(item.href))?.label || 'Dashboard'}
            </h1>
            <Link to="/" className="text-sm text-[#005CB2] hover:underline">
              Siteyi Görüntüle →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
